import type { Tab } from './index';
import { esc, filterTable } from '../utils';
import { fetchSheet } from '../worker';

interface BiotechRow {
  Ticker?:        string;
  TickerURL?:     string;
  Company?:       string;
  SubSector?:     string;
  DateAdded?:     string;
  Price?:         string;
  MarketCap?:     string;
  RevenueTTM?:    string;
  AnalystPT?:     string;
  AnalystPTSub?:  string;
  AnalystPTColor?:string;
  KeyThesis?:     string;
  RiskFactors?:   string;
  Notes?:         string;
  Link1Label?:    string;
  Link1URL?:      string;
  Link2Label?:    string;
  Link2URL?:      string;
  HighlightRow?:  string;
}

export class BiotechTab implements Tab {
  readonly id = 'biotech';
  private loaded = false;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    (this.el('biotech-filter') as HTMLInputElement)
      .addEventListener('input', e => filterTable('biotech-table', (e.target as HTMLInputElement).value));
  }

  render(): void {
    if (!this.loaded) this.load();
  }

  private async load(): Promise<void> {
    const tbody = this.el('biotech-tbody');
    tbody.innerHTML = `<tr><td colspan="12" style="text-align:center;color:var(--text3);padding:24px;letter-spacing:0.1em;font-size:11px;">LOADING…</td></tr>`;
    try {
      const data = await fetchSheet<BiotechRow>('Biotech');
      const rows = data.rows ?? [];
      if (!rows.length) {
        tbody.innerHTML = `<tr><td colspan="12" style="text-align:center;color:var(--text3);padding:24px;font-size:12px;">No data found in Biotech sheet tab.</td></tr>`;
        return;
      }
      tbody.innerHTML = rows.map(r => this.buildRow(r)).join('');
      this.loaded = true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      tbody.innerHTML = `<tr><td colspan="12" style="text-align:center;padding:24px;"><span style="color:var(--red);font-size:12px;">Could not load Biotech data.</span><br><span style="color:var(--text3);font-size:10px;">${esc(msg)}</span></td></tr>`;
    }
  }

  private buildRow(r: BiotechRow): string {
    const tags  = (r.SubSector ?? '').split('|').filter(Boolean).map(t => `<span class="tag">${esc(t.trim())}</span>`).join('');
    const price = (r.Price && r.Price !== 'TBD') ? `<strong>${esc(r.Price)}</strong>` : '<span class="text-muted">TBD</span>';
    const links = [
      r.Link1Label && r.Link1URL ? `<a class="chart-link" href="${esc(r.Link1URL)}" target="_blank" rel="noopener">↗ ${esc(r.Link1Label)}</a>` : '',
      r.Link2Label && r.Link2URL ? `<a class="chart-link" href="${esc(r.Link2URL)}" target="_blank" rel="noopener">↗ ${esc(r.Link2Label)}</a>` : '',
    ].filter(Boolean).join('');

    let aptCell: string;
    if (r.AnalystPTColor === 'warn' || r.AnalystPTColor === 'green') {
      const col = r.AnalystPTColor === 'warn' ? 'var(--warn)' : 'var(--green)';
      aptCell = `<td><span style="color:${col};">${esc(r.AnalystPT ?? '')}</span>${r.AnalystPTSub ? `<div class="text-dim text-small">${esc(r.AnalystPTSub)}</div>` : ''}</td>`;
    } else {
      aptCell = `<td class="text-dim">TBD</td>`;
    }

    const trClass = r.HighlightRow === 'yes' ? ' class="highlight-row"' : '';
    const tickerCell = r.TickerURL
      ? `<span class="ticker-badge"><a href="${esc(r.TickerURL)}" target="_blank" rel="noopener">${esc(r.Ticker ?? '')}</a></span>`
      : `<span class="ticker-badge">${esc(r.Ticker ?? '')}</span>`;

    return `<tr${trClass}>
      <td>${tickerCell}</td>
      <td><strong>${esc(r.Company ?? '—')}</strong></td>
      <td>${tags}</td>
      <td class="text-muted">${esc(r.DateAdded ?? '—')}</td>
      <td>${price}</td>
      <td class="text-muted">${esc(r.MarketCap ?? '—')}</td>
      <td class="text-muted">${esc(r.RevenueTTM ?? '—')}</td>
      ${aptCell}
      <td class="thesis-cell">${esc(r.KeyThesis ?? '—')}</td>
      <td class="risk-cell">${esc(r.RiskFactors ?? '—')}</td>
      <td class="notes-cell">${esc(r.Notes ?? '—')}</td>
      <td><div class="quick-links">${links}</div></td>
    </tr>`;
  }
}
